import React, { useEffect, useState } from 'react';
import { makeStyles } from "@mui/styles";
import { Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select, TextField, InputAdornment, IconButton, Step, Stepper, StepContent, StepLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { Button, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { ScanBarcode, X, LoaderCircle } from 'lucide-react';

import FlagstoneTheme from '../../../Config/flagstoneTheme';
import { searchTypes } from '../../../Config/constants';
import { dataService } from '../../../Services/data/data.service';

export default function SearchDialog(props) {

    const classes = useStyles();
    const { t } = useTranslation();
    const [activeStep, setActiveStep] = useState(1);

    //Step 1: Search
    const [barcode, setBarcode] = useState("");
    const [finishedAfterDate, setFinishedAfterDate] = useState("");
    const [feedbackText, setFeedbackText] = useState("");

    //Step 2: Choose
    const [itemFound, setItemFound] = useState(null);
    const [pallet, setPallet] = useState(null);
    const [product, setProduct] = useState(null);
    const [products, setProducts] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);

    //Step 3: Printing
    const [selectedPrinter, setSelectedPrinter] = useState(props.printers[0].Id);
    const [printPreview, setPrintPreview] = useState(null);
    const [printsQuantity, setPrintsQuantity] = useState(1);
    const [printError, setPrintError] = useState(null);

    /******* START: Returning HTML **********/
    const determineDialogTitle = () => {
        switch (props.searchType) {
            case searchTypes.SSCC:
                return t("Search by SSCC");
            case searchTypes.PRODUCT:
                return t("Search by product");

            default:
                return <div>Error: Invalid step</div>;
        }
    };

    useEffect(() => {
        props.setPrinterSelected(props.printers[0].Id);
    }, [props.printers])

    useEffect(() => {
        setFinishedAfterDate(getDefaultDate());
    }, []);

    const determineInputField = () => {
        switch (props.searchType) {
            case searchTypes.SSCC:
                return t("SSCC-code");
            case searchTypes.PRODUCT:
                return t("EAN-code");

            default:
                return <div>Error: Invalid step</div>;
        }
    };

    const getDefaultDate = () => {
        const today = new Date();
        const twoWeeksAgo = new Date(today);
        twoWeeksAgo.setDate(today.getDate() - 14);

        const year = twoWeeksAgo.getFullYear();
        const month = String(twoWeeksAgo.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed
        const day = String(twoWeeksAgo.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    };

    const formatDate = (date) => {
        var d = new Date(date);

        // Get the day, month, and year
        let day = d.getDate();
        let month = d.getMonth() + 1;
        let year = d.getFullYear();

        // Pad the day and month with leading zeros if necessary
        day = day < 10 ? '0' + day : day;
        month = month < 10 ? '0' + month : month;

        // Return the formatted date string
        return day + "/" + month + "/" + year;
    };
    /******* END: Returning HTML **********/

    /******* START STEP 1: SEARCH **********/
    const changeBarcode = (value) => {
        var tempBadgeNumber = value.target.value;
        setBarcode(tempBadgeNumber);
    };

    const changeFinishedAfterDate = (value) => {
        // date format: yyyy-mm-dd string
        setFinishedAfterDate(value.target.value.toString());
    };

    const _handleKeyDownBarcode = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleSearch = () => {
        setPrintError(null);
        switch (props.searchType) {
            case searchTypes.SSCC:
                setPallet(null);
                dataService.getPalletDataBySSCC(barcode, props.workstationId, props.userId !== null && props.userId !== undefined ? props.userId : -1).then(result => {
                    if (result !== '' && result !== null && result !== undefined && result.ok && result.data.Succeeded) {
                        setItemFound(true);
                        setActiveStep(2);
                        setPallet(result.data.Results[0].PalletModel);
                        setFeedbackText(null);
                    } else {
                        setFeedbackText(t("No pallets found with this SSCC-code, try again with another code."));
                        setItemFound(false);
                    }

                });
                return t("SSCC-code");
            case searchTypes.PRODUCT:
                setProducts(null);
                setSelectedOrder(null);
                let tempFinishedAfterDate = ""
                // handle if finishedAfterDate is empty -> 01-01-1900
                if (finishedAfterDate === "") {
                    tempFinishedAfterDate = "1900-01-01";
                } else {
                    tempFinishedAfterDate = finishedAfterDate;
                }

                dataService.getProductionOrdersByEAN(barcode, props.workstationId, props.userId !== null && props.userId !== undefined ? props.userId : -1, tempFinishedAfterDate).then(result => {
                    if (result !== '' && result !== null && result !== undefined && result.ok && result.data.Succeeded && result.data.Results[0].ProductionOrders !== null && result.data.Results[0].ProductionOrders.length > 0) {
                        setItemFound(true);
                        setActiveStep(2);
                        setProducts(result.data.Results[0].ProductionOrders);
                        setFeedbackText(null);
                    } else {
                        setFeedbackText(t(`No products found with this EAN-code that are produced after` + " " + formatDate(tempFinishedAfterDate)));
                        setItemFound(false);
                    }
                });
                return t("EAN-code");

            default:
                return <div>Error: Invalid step</div>;
        }
    };
    /******* END STEP 1: SEARCH **********/

    /******* START STEP 2: SELECT LABEL **********/
    const handleGetPreview = (selectedProduct) => {
        var tempUserId = props.userId !== null && props.userId !== undefined ? props.userId : -1;
        switch (props.searchType) {
            case searchTypes.SSCC:
                dataService.getPrintPreviewBySSCC(pallet.sscc, props.workstationId, tempUserId).then(result => {
                    if (result !== '' && result !== null && result !== undefined && result.ok) {
                        setPrintPreview(result.data);
                        props.setPrintPreview(result.data);
                        setActiveStep(3);

                    } else {
                        //Problemen
                    }
                }).catch((error) => {
                    console.log('error ', error)
                });
                return true;
            case searchTypes.PRODUCT:
                if (product !== null && product !== undefined && product !== false) {
                    dataService.getPrintPreviewBySSCC(product.SSCC, props.workstationId, tempUserId).then(result => {
                        if (result !== '' && result !== null && result !== undefined && result.ok) {
                            setPrintPreview(result.data);
                            props.setPrintPreview(result.data);

                            setActiveStep(3);

                        } else {
                            //Problemen
                        }
                    }).catch((error) => {
                        console.log('error ', error)
                    });
                } else if (products !== null && products !== undefined && products !== false) {
                    dataService.getPrintPreviewBySSCC(selectedProduct.sscc, props.workstationId, tempUserId).then(result => {
                        if (result !== '' && result !== null && result !== undefined && result.ok) {
                            setPrintPreview(result.data);
                            props.setPrintPreview(result.data);

                            setActiveStep(3);

                        } else {
                            //Problemen
                        }
                    }).catch((error) => {
                        console.log('error ', error)
                    });
                }
                return true;
            default:
                return <div>Error: Invalid step</div>;
        }
    };

    const handleGoBack = () => {
        setSelectedOrder(null);
        setPrintError(null);
    };

    const handleSelectOrder = (order) => {
        props.setOrderId(order.OrderId);
        props.setProductId(order.ProductId);
        setSelectedOrder(order);
        setActiveStep(3);
    };

    /******* END STEP 2: SELECT LABEL **********/

    /******* START STEP 3: HANDLE NEW SSCC **********/
    const handleNewLabel = () => {
        var tempUserId = props.userId !== null && props.userId !== undefined ? props.userId : -1;
        dataService.getNewPalletNumber(selectedOrder.Reference, props.workstationId, tempUserId, selectedOrder.Quantity).then(result => {
            if (result !== '' && result !== null && result !== undefined && result.ok) {
                if (result.data.Succeeded) {
                    //Carrier gevonden, doe het nodige...
                    props.setNewSSCC(JSON.parse(result.data.Results).CarrierBarcode);
                    props.setProductId(selectedOrder.ProductId);
                    props.setOrderId(selectedOrder.OrderId);
                    props.openAuthenticateDialog();
                } else {
                    setPrintError(t("Geen pallet gevonden"));
                    props.setSubmitting(false);
                }
            } else {
                //Problemen
                setPrintError(t("An error occurred while generating a new SSCC code."));
                props.setSubmitting(false);
            }

        }).catch((error) => {
            console.log('error ', error)
            setPrintError(error);
            props.setSubmitting(false);
        });
    };

    /******* END STEP 3: HANDLE NEW SSCC **********/

    /******* START STEP 4: HANDLE PRINTING **********/
    const handlePrinterSelection = (e) => {
        setSelectedPrinter(e.target.value);
        props.setPrinterSelected(e.target.value);
    };

    const handlePrintsQuantity = (quantity) => {
        setPrintsQuantity(quantity);
        props.setPrintsQuantity(quantity)
    };

    const handlePrint = () => {
        if (props.submitting) return;
        props.setSubmitting(true);
        if (props.searchType === searchTypes.SSCC) {
            props.setNewSSCC(pallet.SSCC);
            props.setProductId(pallet.ProductId);
            props.setOrderId(pallet.OrderId);
            props.openAuthenticateDialog();

        } else if (props.searchType === searchTypes.PRODUCT) {
            handleNewLabel();
        }
    };
    /******* END STEP 4: HANDLE PRINTING **********/

    return (

        <Dialog className={classes.dialog}
            open={props.dialogOpen}
            onClose={props.closeDialog}

            classes={{ paper: classes.dialogPaper }}

            aria-labelledby="draggable-dialog-title"
        >
            <div className={classes.dialog__head}>
                <DialogTitle className={classes.dialog__title} id="draggable-dialog-title">
                    {!selectedOrder && !pallet ? determineDialogTitle() : t("Print label")}
                </DialogTitle>
                <button onClick={() => props.closeDialog()} className={classes.dialog__closeButton}>
                    <X size={19} />
                </button>
            </div>
            <DialogContent className={classes.dialog__contentContainer}>
                <div className={classes.dialog__content}>
                    <div className={classes.dialog__search}>
                        <div className={classes.dialog__inputContainer}>
                            <label className={classes.dialog__label}>{determineInputField()}</label>
                            <TextField
                                id="outlined-search"
                                className={classes.dialog__searchField}
                                variant="outlined"
                                value={barcode}
                                autoFocus
                                onChange={(e) => changeBarcode(e)}
                                onKeyDown={(e) => _handleKeyDownBarcode(e)}
                                inputProps={{
                                    style: {
                                        height: '40px',
                                        fontFamily: "Lato, sans-serif",
                                        fontSize: '16px',
                                        color: '#0C0E29',
                                        padding: '0px',
                                        paddingLeft: '14px',
                                    },
                                }}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end"><ScanBarcode size={24} color='#0C0E29' /></InputAdornment>,
                                }}
                            >
                            </TextField>
                        </div>

                        {/* Datefield for finishedAfterDate */}
                        {props.searchType === searchTypes.PRODUCT &&
                            <div className={classes.dialog__inputContainer} style={{ width: '156px' }}>
                                <label className={classes.dialog__label}>{t("Produced after")}</label>
                                <TextField
                                    id="date"
                                    type="date"
                                    value={finishedAfterDate}
                                    onChange={(e) => changeFinishedAfterDate(e)}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    InputProps={{
                                        style: {
                                            height: '40px',
                                            fontFamily: "Lato, sans-serif",
                                            fontSize: '16px',
                                            color: '#0C0E29',
                                        },
                                    }}
                                />
                            </div>
                        }
                        <Button className={classes.dialog__searchButton} disabled={barcode === "" || barcode === null || barcode === undefined} onClick={() => handleSearch()}>
                            {t('Search')}
                        </Button>
                    </div>
                    {/* No orders found */}
                    {products === null && selectedOrder === null && pallet === null &&
                        <div className={classes.dialog__emptyState}>
                            {feedbackText !== "" && feedbackText !== null ?
                                <p className={classes.dialog__emptyState__feedback}>{feedbackText}</p> :
                                <p className={classes.dialog__emptyState__text}>{determineDialogTitle()}</p>
                            }
                        </div>
                    }
                    {/* selectionList - Search by Product */}
                    {selectedOrder === null && products !== null && products !== undefined && products.length > 0 &&
                        <div className={classes.dialog__productSelectionContainer}>
                            <table className={classes.dialog__table}>
                                <thead className={classes.dialog__tableHead}>
                                    <tr className={classes.dialog__tableRow} style={{ position: 'relative' }}>
                                        <th className={classes.dialog__selectionTableHeadRow__indicator}></th>
                                        <th className={classes.dialog__tableHeader}>#</th>
                                        <th className={classes.dialog__tableHeader}>{t("Description")}</th>
                                        <th className={classes.dialog__tableHeader} style={{ textAlign: 'right' }}>{t("Production date")}</th>
                                    </tr>
                                </thead>
                                <tbody className={classes.dialog__tableBody}>
                                    {products.map((product, index) => {
                                        return (
                                            <tr className={classes.dialog__selectionTableRow} key={"product_" + index} onClick={() => handleSelectOrder(product)}>
                                                <td className={classes.dialog__selectionTableRow__indicator}></td>
                                                <td className={classes.dialog__tableData}>{product.Reference}</td>
                                                <td className={classes.dialog__tableData} style={{ overflow: 'hidden', textWrap: 'nowrap', textOverflow: 'ellipsis', maxWidth: '400px' }}>{product.NameFR}</td>
                                                <td className={classes.dialog__tableData} style={{ textAlign: 'right' }}>{product.FinishedOn ? formatDate(product.FinishedOn) : '-'}</td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    }
                    {/* searchResult - Search by Product */}
                    {props.searchType === searchTypes.PRODUCT && selectedOrder !== null &&
                        <div className={classes.dialog__printContainer}>
                            <table className={classes.dialog__table}>
                                <thead className={classes.dialog__tableHead}>
                                    <tr className={classes.dialog__tableRow}>
                                        <th className={classes.dialog__tableHeader}>#</th>
                                        <th className={classes.dialog__tableHeader}>{t("Description")}</th>
                                        <th className={classes.dialog__tableHeader} style={{ textAlign: 'right' }}>{t("Production date")}</th>
                                    </tr>
                                </thead>
                                <tbody className={classes.dialog__tableBody}>
                                    <tr className={classes.dialog__tableRow}>
                                        <td className={classes.dialog__tableData}>{selectedOrder.Reference}</td>
                                        <td className={classes.dialog__tableData} style={{ overflow: 'hidden', textWrap: 'nowrap', textOverflow: 'ellipsis', maxWidth: '400px' }}>{selectedOrder.NameFR}</td>
                                        <td className={classes.dialog__tableData} style={{ textAlign: 'right' }}>{selectedOrder.FinishedOn ? formatDate(selectedOrder.FinishedOn) : '-'}</td>
                                    </tr>
                                </tbody>
                            </table>
                            {printError ?
                                <div className={classes.dialog__printErrorContainer}>
                                    <p className={classes.dialog__printError}>{t(printError)}</p>
                                </div>
                                :
                                <div className={classes.dialog__printSettingsContainer}>
                                    <div className={classes.dialog__printSetting}>
                                        <label className={classes.dialog__printSettingsLabel}>{t("Select a printer")}</label>
                                        <Select
                                            className={classes.dialog__printSettingsField}
                                            labelId="demo-simple-select-label"
                                            id="demo-simple-select"
                                            value={selectedPrinter}
                                            disabled
                                            onChange={handlePrinterSelection}
                                            sx={{ width: '100%' }}
                                        >
                                            {props.printers.map((printer, index) => {
                                                return <MenuItem value={printer.Id} key={"printer_" + index}><b style={{ paddingRight: '5px' }}>{printer.Code}:</b> {printer.Name}</MenuItem>
                                            })}
                                        </Select>
                                    </div>
                                    <div className={classes.dialog__printSetting}>
                                        <label className={classes.dialog__printSettingsLabel}>{t("Number of prints")}</label>
                                        <TextField
                                            className={classes.dialog__printSettingsField}
                                            id="boxes"
                                            type="number"
                                            value={printsQuantity}
                                            onChange={(e) => handlePrintsQuantity(e.target.value)}
                                            sx={{ width: '100%' }}
                                            InputProps={{
                                                style: {
                                                    height: '40px',
                                                    fontFamily: "Lato, sans-serif",
                                                    fontSize: '16px',
                                                    color: '#0C0E29',
                                                },
                                            }}
                                        />
                                    </div>
                                </div>
                            }
                        </div>}
                    {/* searchResult - Search by SSCC */}
                    {props.searchType === searchTypes.SSCC && pallet !== null &&
                        <div className={classes.dialog__printContainer}>
                            <table className={classes.dialog__table}>
                                <thead className={classes.dialog__tableHead}>
                                    <tr className={classes.dialog__tableRow}>
                                        <th className={classes.dialog__tableHeader}>#</th>
                                        <th className={classes.dialog__tableHeader}>{t("Product nr.")}</th>
                                        <th className={classes.dialog__tableHeader}>{t("Description")}</th>
                                        <th className={classes.dialog__tableHeader} style={{ textAlign: 'right' }}>{t("Quantity")}</th>
                                        <th className={classes.dialog__tableHeader}>UOM</th>
                                    </tr>
                                </thead>
                                <tbody className={classes.dialog__tableBody}>
                                    <tr className={classes.dialog__tableRow}>
                                        <td className={classes.dialog__tableData}>{pallet.ProductionOrderReference}</td>
                                        <td className={classes.dialog__tableData}>{pallet.ProductCode}</td>
                                        <td className={classes.dialog__tableData} style={{ overflow: 'hidden', textWrap: 'nowrap', textOverflow: 'ellipsis', maxWidth: '200px' }} >{pallet.ProductNameFR}</td>
                                        <td className={classes.dialog__tableData} style={{ textAlign: 'right' }}>{pallet.Quantity}</td>
                                        <td className={classes.dialog__tableData}>{pallet.QuantityFactor}</td>
                                    </tr>
                                </tbody>
                            </table>
                            <div className={classes.dialog__printSettingsContainer}>
                                <div className={classes.dialog__printSetting}>
                                    <label className={classes.dialog__printSettingsLabel}>{t("Select a printer")}</label>
                                    <Select
                                        className={classes.dialog__printSettingsField}
                                        labelId="demo-simple-select-label"
                                        id="demo-simple-select"
                                        value={selectedPrinter}
                                        disabled
                                        onChange={handlePrinterSelection}
                                        sx={{ width: '100%' }}
                                    >
                                        {props.printers.map((printer, index) => {
                                            return <MenuItem value={printer.Id} key={"printer_" + index}><b style={{ paddingRight: '5px' }}>{printer.Code}:</b> {printer.Name}</MenuItem>
                                        })}
                                    </Select>
                                </div>
                                <div className={classes.dialog__printSetting}>
                                    <label className={classes.dialog__printSettingsLabel}>{t("Number of prints")}</label>
                                    <TextField
                                        className={classes.dialog__printSettingsField}
                                        id="boxes"
                                        type="number"
                                        value={printsQuantity}
                                        onChange={(e) => handlePrintsQuantity(e.target.value)}
                                        sx={{ width: '100%' }}
                                        InputProps={{
                                            style: {
                                                height: '40px',
                                                fontFamily: "Lato, sans-serif",
                                                fontSize: '16px',
                                                color: '#0C0E29',
                                            },
                                        }}
                                    />
                                </div>
                            </div>
                        </div>}
                </div>
            </DialogContent >
            <DialogActions className={classes.dialog__buttonContainer}>
                {pallet === null && selectedOrder === null ?
                    "" :
                    <>
                        {props.searchType === searchTypes.PRODUCT ? <Button className={classes.dialog__button__secondary} onClick={() => handleGoBack()}>
                            {t('Back')}
                        </Button> : null}

                        <button className={`${classes.dialog__button} ${props.submitting ? classes.dialog__button_loading : ""}`} disabled={props.printerSelected === null || printsQuantity < 1 || printError} onClick={() => handlePrint()}>
                            {props.submitting && <div className={classes.button__loader}><LoaderCircle className={classes.button__loader__spinner} /></div>}
                            {props.buttonText ? props.buttonText : t('Print')}
                        </button>
                    </>
                }
            </DialogActions>
        </Dialog >
    );
}

const useStyles = makeStyles(() => ({

    dialog: {
        // width: "720px !important",
        // height: "384px !important",
        margin: "auto"
    },

    dialogPaper: {
        minWidth: '720px',
        minHeight: '384px',
        maxWidth: 'calc(100vw - 512px) !important',
        width: '800px !important',
        height: '480px !important',
        borderRadius: '6px !important',
    },

    dialog__head: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',

        padding: '16px 16px 0px 16px !important',
        marginBottom: '8px !important',
    },

    dialog__title: {
        fontSize: '24px !important',
        fontFamily: "Lato, sans-serif !important",
        fontWeight: 'normal',

        padding: '0px !important',
    },

    dialog__closeButton: {
        backgroundColor: '#F5F5F5',
        border: 'none',
        cursor: 'pointer',

        padding: '3px !important',
        height: '25px !important',

        borderRadius: '3px',

        transition: 'backgroundColor 0.2s, color 0.2s',

        '&:hover': {
            backgroundColor: '#D84949',
            color: '#F5F5F5',
        },

        '&:focus': {
            outline: '#4654FB auto 1px',
        },

    },

    dialog__contentContainer: {
        padding: '0px 16px !important',

        // remove scrollbar
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        '-ms-overflow-style': 'none',
        scrollbarWidth: 'none',

    },

    dialog__content: {
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',

        margin: '0px !important',
    },

    dialog__searchContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },

    dialog__searchField: {
        width: '100%',
        height: '40px !important',
        padding: '0px !important',
    },

    dialog__inputContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },

    dialog__label: {
        fontSize: '12px',
        fontFamily: "Lato, sans-serif",
        fontWeight: 'normal',
        color: '#0C0E29',
    },

    dialog__search: {
        width: '100%',
        display: 'flex',
        gap: '8px',
        alignItems: 'flex-end',
    },

    dialog__searchButton: {
        color: "white !important",
        fontSize: "16px !important",
        fontWeight: "bold !important",
        fontFamily: "Lato, sans-serif !important",
        backgroundColor: FlagstoneTheme.palette.primary.main + " !important",
        border: "1px solid " + FlagstoneTheme.palette.primary.main + " !important",
        minWidth: "fit-content !important",
        height: '40px !important',
        padding: '0px 20px !important',
        textTransform: 'capitalize !important',

        '&:hover': {
            backgroundColor: "white !important",
            color: FlagstoneTheme.palette.primary.main + " !important",
            border: "1px solid " + FlagstoneTheme.palette.primary.main + " !important",
        },
        '&:disabled': {
            backgroundColor: "lightgrey !important",
            color: "white !important",
            border: "1px solid darkgrey !important",
            cursor: "not-allowed !important"
        },
    },

    dialog__emptyState: {
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: '96px',
    },

    dialog__emptyState__text: {
        fontSize: '20px',
        fontFamily: "Lato, sans-serif",
        fontWeight: 'normal',
        color: '#A9A9A9',
    },

    dialog__emptyState__feedback: {
        fontSize: '20px',
        fontFamily: "Lato, sans-serif",
        fontWeight: 'normal',
        color: '#D84949',
    },

    dialog__printContainer: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: '16px',
        margin: '16px 0',
    },

    dialog__productSelectionContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        marginTop: '16px',
    },

    dialog__table: {
        width: '100%',
        borderCollapse: 'collapse',

        fontSize: '16px',
        fontFamily: "Lato, sans-serif",
        color: '#0C0E29',
    },

    dialog__tableHead: {
        width: '100%',
        backgroundColor: '#EDEDED',
        border: 'none',
        borderRadius: '3px',
    },

    dialog__tableRow: {
        maxWidth: '100%',
    },

    dialog__selectionTableRow: {
        maxWidth: '100%',
        cursor: 'pointer',

        transition: 'backgroundColor 0.3s',

        position: 'relative',

        '&:hover': {
            backgroundColor: '#F5F5F5',

            '& $dialog__selectionTableRow__indicator': {
                backgroundColor: FlagstoneTheme.palette.primary.main,
            },
        },
    },

    dialog__selectionTableHeadRow__indicator: {
        position: 'absolute',
        top: '0px',
        left: '0px',
    },

    dialog__selectionTableRow__indicator: {
        position: 'absolute',
        top: '0px',
        left: '0px',
        width: '3px',
        height: '100%',
        backgroundColor: 'transparent',
        borderRadius: '3px 0px 0px 3px',

        transition: 'backgroundColor 0.3s',
    },

    dialog__tableHeader: {
        textAlign: 'left',
        padding: '8px 12px !important',

        fontWeight: 'normal',

        // first child and second  child border radiuis
        '&:first-child': {
            borderRadius: '3px 0px 0px 3px',
            paddingLeft: '14px'
        },
        '&:last-child': {
            borderRadius: '0px 3px 3px 0px'
        },
    },

    dialog__tableBody: {
        width: '100%',

        '&:before': {
            content: '""',
            display: 'block',
            height: '8px',
            backgroundColor: 'transparent',
        },
    },

    dialog__tableData: {
        textAlign: 'left',
        padding: '8px 12px !important',

        fontWeight: 'normal',

        // first child and second  child border radiuis
        '&:first-child': {
            borderRadius: '3px 0px 0px 3px',
            paddingLeft: '14px'
        },
        '&:last-child': {
            borderRadius: '0px 3px 3px 0px'
        },
    },

    dialog__printErrorContainer: {
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '12px',
        borderRadius: '6px',
        border: "1px solid #D84949",
        backgroundColor: '#fbe5e5',
        boxSizing: 'border-box',
    },

    dialog__printError: {
        fontSize: '16px',
        fontFamily: "Lato, sans-serif",
        fontWeight: 'normal',
        color: '#D84949',
    },

    dialog__printSettingsContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        gap: '8px',
    },

    dialog__printSetting: {
        width: '50%',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },

    dialog__printSettingsLabel: {
        fontSize: '16px',
        fontFamily: "Lato, sans-serif",
        fontWeight: 'normal',
        color: '#0C0E29',
    },

    dialog__printSettingsField: {
        width: '100%',
        height: '40px !important',
        padding: '0px !important',
    },


    dialog__buttonContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between !important',
        width: "100% !important",
        boxSizing: 'border-box',

        padding: '16px !important',
        paddingTop: '0px !important',
        margin: '0px !important',
    },

    dialog__button__secondary: {
        color: FlagstoneTheme.palette.primary.main + " !important",
        fontSize: "16px !important",
        fontWeight: "bold !important",
        fontFamily: "Lato, sans-serif !important",
        backgroundColor: "white !important",
        border: "1px solid " + FlagstoneTheme.palette.primary.main + " !important",
        minWidth: "fit-content !important",
        height: '40px !important',
        padding: '0px 20px !important',
        textTransform: 'capitalize !important',

        '&:hover': {
            backgroundColor: FlagstoneTheme.palette.primary.main + " !important",
            color: "white !important",
            border: "1px solid " + FlagstoneTheme.palette.primary.main + " !important",
        },
        '&:disabled': {
            backgroundColor: "lightgrey !important",
            color: "white !important",
            border: "1px solid darkgrey !important",
            cursor: "not-allowed !important"
        },
    },

    buttons: {
        textAlign: "center"
    },
    stepButton: {
        zIndex: 1,
        color: "#fff",
        width: 50,
        height: 50,
        display: "flex",
        borderRadius: "50%",
        justifyContent: "center",
        alignItems: "center",
    },

    dialog__button: {
        color: "white !important",
        fontSize: "16px !important",
        fontWeight: "bold !important",
        fontFamily: "Lato, sans-serif !important",
        backgroundColor: FlagstoneTheme.palette.primary.main + " !important",
        border: "none !important",
        borderRadius: "3px !important",
        minWidth: "fit-content !important",
        height: '40px !important',
        padding: '0px 20px !important',
        textTransform: 'none !important',
        position: 'relative !important',

        marginLeft: 'auto !important',

        transition: 'all 0.2s !important',

        '&:hover': {
            backgroundColor: "#3B3AF1 !important",
            color: "white !important",
            cursor: "pointer !important",
        },
        '&:disabled': {
            backgroundColor: "lightgrey !important",
            color: "white !important",
            border: "1px solid darkgrey !important",
            cursor: "not-allowed !important"
        },
    },

    dialog__button_loading: {
        backgroundColor: "#C3D3FF !important",
        color: "#C3D3FF !important",

        '&:hover': {
            backgroundColor: "#C3D3FF !important",
            color: "#C3D3FF !important",
            cursor: "default !important",
        },
    },

    button__loader: {
        width: "24px",
        height: "24px",
        color: FlagstoneTheme.palette.primary.main,

        position: "absolute",
        display: "block",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
    },

    button__loader__spinner: {
        width: "100%",
        height: "100%",

        animation: "spin 1s linear infinite",
    },

    '@global': {
        '@keyframes spin': {
            '0%': {
                transform: 'rotate(0deg)',
            },
            '100%': {
                transform: 'rotate(360deg)',
            },
        },
    },

    searchButton: {
        color: "white !important",
        fontSize: "14px",
        fontFamily: "Lato, sans-serif",
        fontWeight: "bold !important",
        backgroundColor: FlagstoneTheme.palette.primary.main + " !important",
        border: "1px solid " + FlagstoneTheme.palette.primary.main + " !important",
        minWidth: "144px",

        '&:hover': {
            backgroundColor: "white !important",
            color: FlagstoneTheme.palette.primary.main + " !important",
            border: "1px solid " + FlagstoneTheme.palette.primary.main + " !important",
        },
        '&:disabled': {
            backgroundColor: "lightgrey !important",
            color: "white !important",
            border: "1px solid darkgrey !important",
            cursor: "not-allowed !important"
        },
    },

    closeButton: {
        color: FlagstoneTheme.palette.primary.main + ' !important',
        fontSize: "14px",
        fontFamily: "Lato, sans-serif",
        fontWeight: 'bold !important',
        backgroundColor: "white !important",
        marginRight: '15px !important',
        border: "1px solid " + FlagstoneTheme.palette.primary.main + ' !important',
        width: "144px",

        '&:hover': {
            backgroundColor: FlagstoneTheme.palette.primary.main + ' !important',
            color: "white !important",
            border: "1px solid " + FlagstoneTheme.palette.primary.main + ' !important',
        },
    },

    resultText: {
        fontSize: '13px',
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        color: FlagstoneTheme.palette.error.main,
        fontWeight: '500',
        height: 'fit-content',
        width: '100%',
        marginBottom: "12px"
    },
    dialogContext: {
        color: "#000000",
        fontSize: "18px",
        fontFamily: "Lato, sans-serif",
        fontWeight: 'medium',
        textAlign: "center"
    }, uiTextField: {
        backgroundColor: "white",
        marginBottom: "10px !important"
    },
}));
